import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useUI } from './UIContext';
import { useMessageContext, createContextProvider } from './MessageContext';
import { getContentTypeConfig } from '../components/content/ContentTypeRegistry';
import { useConfig } from './ConfigContext';

/**
 * ResourceContextProvider Component - Adapted for Tabs
 * Provides a default/fallback resource context based on the *active* tab.
 * This context has a lower priority than view-specific contexts.
 */
const ResourceContextProvider = ({ children }) => {
  const { openResources, activeResourceId } = useUI();
  const { registerContextProvider } = useMessageContext();
  const { includeResourceContextInChat = true } = useConfig(); // Default to true

  const activeResource = useMemo(() => {
    if (!activeResourceId) return null;
    return openResources.find(res => res.id === activeResourceId);
  }, [openResources, activeResourceId]);

  useEffect(() => {
    const provider = createContextProvider('resource-tab-generic', {
      isActive: () => !!activeResource,
      getContextValue: () => {
        // CONFIG CHECK: If disabled, do not inject the [RESOURCE] tag
        if (includeResourceContextInChat === false) {
          return null;
        }

        if (!activeResource) return null;
        if (activeResource.type === 'learning_paths_dashboard') {
            return null;
        }
        const { handler } = getContentTypeConfig(activeResource.type);
        if (handler?.includeResourceTagInChat === false) {
          return null;
        }
        return `[RESOURCE]${activeResource.id}[/RESOURCE]`;
      },
      getDescription: () => {
        if (!activeResource || activeResource.type === 'learning_paths_dashboard') return null;
        const { getTitle } = getContentTypeConfig(activeResource.type);
        return {
          text: getTitle(activeResource),
          icon: <MenuBookIcon fontSize="small" />,
          type: 'resource-tab'
        };
      },
      priority: 10
    });

    const unregister = registerContextProvider(provider);
    return unregister;
  }, [activeResource, registerContextProvider, includeResourceContextInChat]);

  return children;
};

ResourceContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default ResourceContextProvider;